export type PrepareImageForUploadOptions = {
  /**
   * Maximum width/height of the output image (keeps aspect ratio).
   */
  maxDimension?: number;
  /**
   * Attempt to keep encoded output under this many bytes by lowering JPEG quality.
   * If unset, a single encode pass is done.
   */
  maxBytes?: number;
  /**
   * Initial JPEG quality for encoding (0..1).
   */
  quality?: number;
};

type DecodedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  revoke?: () => void;
  close?: () => void;
};

async function decodeImage(file: File): Promise<DecodedImage> {
  const canUseCreateImageBitmap = typeof window !== "undefined" && "createImageBitmap" in window;
  if (canUseCreateImageBitmap) {
    const bitmap = await createImageBitmap(file);
    return {
      source: bitmap,
      width: bitmap.width || 1,
      height: bitmap.height || 1,
      close: () => bitmap.close(),
    };
  }

  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = "async";
  img.src = url;
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to decode image"));
    });
    return {
      source: img,
      width: img.naturalWidth || img.width || 1,
      height: img.naturalHeight || img.height || 1,
      revoke: () => URL.revokeObjectURL(url),
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode image"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      quality,
    );
  });
}

/**
 * Prepare an image file for upload by downscaling and JPEG recompressing.
 * Falls back to returning the original file if decoding/encoding is not possible.
 */
export async function prepareImageForUpload(file: File, opts: PrepareImageForUploadOptions = {}): Promise<File> {
  const maxDimension = opts.maxDimension ?? 1200;
  const maxBytes = opts.maxBytes;
  const initialQuality = Math.min(1, Math.max(0.4, opts.quality ?? 0.85));
  const minQuality = 0.25;
  const minDimension = 480;

  if (!file.type.startsWith("image/")) return file;

  let decoded: DecodedImage | null = null;
  try {
    decoded = await decodeImage(file);
    const decodedImage = decoded;

    const srcW = decodedImage.width || 1;
    const srcH = decodedImage.height || 1;
    const scale = Math.min(1, maxDimension / Math.max(srcW, srcH));
    let outW = Math.max(1, Math.round(srcW * scale));
    let outH = Math.max(1, Math.round(srcH * scale));

    // If already small enough (dimensions + optional byte cap), keep the original file.
    if (scale === 1 && (maxBytes === undefined || file.size <= maxBytes)) {
      return file;
    }

    const canvas = document.createElement("canvas");
    const redraw = () => {
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return false;
      ctx.drawImage(decodedImage.source, 0, 0, outW, outH);
      return true;
    };
    if (!redraw()) return file;

    let quality = initialQuality;
    let blob = await canvasToBlob(canvas, quality);
    let bestBlob: Blob = blob;
    let bestSize = blob.size;

    const considerBest = (b: Blob) => {
      if (b.size < bestSize) {
        bestBlob = b;
        bestSize = b.size;
      }
    };
    considerBest(blob);
    if (maxBytes !== undefined) {
      // Try quality reductions first, then downscale further if needed.
      for (let attempt = 0; attempt < 14 && blob.size > maxBytes; attempt += 1) {
        if (quality > minQuality) {
          quality = Math.max(minQuality, quality - 0.12);
          blob = await canvasToBlob(canvas, quality);
          considerBest(blob);
          continue;
        }

        if (Math.max(outW, outH) <= minDimension) break;
        outW = Math.max(minDimension, Math.round(outW * 0.85));
        outH = Math.max(minDimension, Math.round(outH * 0.85));
        quality = initialQuality;
        if (!redraw()) break;
        blob = await canvasToBlob(canvas, quality);
        considerBest(blob);
      }
    }

    // If a maxBytes target was provided and the original violates it, prefer the smallest generated output,
    // even if it's not smaller than the original (still helps avoid proxy limits).
    const chosen = maxBytes !== undefined && file.size > maxBytes ? bestBlob : blob;
    if (chosen.size >= file.size && (maxBytes === undefined || file.size <= maxBytes)) return file;

    const safeBaseName = file.name.replace(/\.[^/.]+$/, "") || "image";
    return new File([chosen], `${safeBaseName}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    decoded?.revoke?.();
    decoded?.close?.();
  }
}

